import { Component, inject, Output, EventEmitter, OnDestroy, OnInit, effect, Injector, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil, of } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { ThemeService, ThemePreference } from '../../core/services/theme.service';
import { BranchContextService } from '../../core/services/branch-context.service';
import { NotificationDropdownComponent } from '../../features/settings/components/notification-dropdown/notification-dropdown.component';
import { GlobalSearchService, SearchResult } from '../../core/services/global-search.service';
import { TaskModalService } from '../../features/tasks/services/task-modal.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NotificationDropdownComponent],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ])
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() sidebarToggle = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private blurTimeout: any;
  private injector = inject(Injector);
  private cdr = inject(ChangeDetectorRef); // Add ChangeDetectorRef

  themeService = inject(ThemeService);
  authService = inject(AuthService);
  searchService = inject(GlobalSearchService);
  branchContext = inject(BranchContextService);
  router = inject(Router);
  taskModalService = inject(TaskModalService);

  searchQuery = '';
  showSearchResults = false;
  searchResults: SearchResult[] = [];
  isSearching = false;
  showUserMenu = false;
  notificationCount = 1;
  themeOptions: ThemePreference[] = ['light', 'dark', 'system'];

  constructor() {
    // Setup search with debounce
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          this.showSearchResults = false;
          return of([]);
        }

        this.isSearching = true;
        return this.searchService.search(query);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSearching = false;
        this.searchResults = this.formatSearchResults(response);
        this.showSearchResults = this.searchResults.length > 0;
      },
      error: (error) => {
        this.isSearching = false;
        console.error('Search error:', error);
        this.showSearchResults = false;
      }
    });

    // Setup reactive effect to watch for branch context changes
    effect(() => {
      const branches = this.branchContext.availableBranches();
      const selectedBranch = this.branchContext.selectedBranchId();
      
      // console.log('Header: Branch context changed');
      // console.log('Available branches:', branches.length);
      // console.log('Selected branch:', selectedBranch);
      
      // IMPORTANT: Force change detection
      this.cdr.detectChanges();
    }, { injector: this.injector });

    // Also watch for auth changes
    effect(() => {
      const user = this.authService.currentUser();
      // console.log('Header: Auth state changed, user:', user?.username);
      
      // When user logs in, ensure branch context is initialized
      if (user && this.branchContext.availableBranches().length === 0) {
        console.log('Header: User logged in but no branches, initializing...');
        this.initializeBranchContext();
      }
      
      // Force change detection
      this.cdr.detectChanges();
    }, { injector: this.injector });
  }

  async ngOnInit(): Promise<void> {
    console.log('Header ngOnInit called');
    
    // Only initialize if user is already authenticated
    if (this.authService.currentUser()) {
      await this.initializeBranchContext();
      this.cdr.detectChanges(); // Force change detection after init
    }
  }

  private async initializeBranchContext(): Promise<void> {
    try {
      console.log('Header: Initializing branch context...');
      await this.branchContext.waitUntilReady();
      console.log('Header: Branch context ready');
      console.log('Header: Available branches:', this.branchContext.availableBranches().length);
      this.cdr.detectChanges(); // Force change detection after ready
    } catch (error) {
      console.error('Header: Error initializing branch context:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.searchSubject.next(this.searchQuery);
  }

  private formatSearchResults(response: any): SearchResult[] {
    const results: SearchResult[] = [];

    if (response.leads) {
      response.leads.forEach((lead: any) => {
        results.push({
          id: lead.id,
          name: lead.name,
          type: 'Lead',
          context: lead.context,
          route: `/leads/${lead.id}`
        });
      });
    }

    if (response.clients) {
      response.clients.forEach((client: any) => {
        results.push({
          id: client.id,
          name: client.name,
          type: 'Client',
          context: client.context,
          route: `/clients/${client.id}`
        });
      });
    }

    if (response.opportunities) {
      response.opportunities.forEach((opportunity: any) => {
        results.push({
          id: opportunity.id,
          name: opportunity.name,
          type: 'Opportunity',
          context: opportunity.context,
          route: `/opportunities/${opportunity.id}`
        });
      });
    }

    if (response.tasks) {
      response.tasks.forEach((task: any) => {
        results.push({
          id: task.id,
          name: task.name,
          type: 'Task',
          context: task.context,
          route: `/tasks/${task.id}`
        });
      });
    }

    return results;
  }

  onResultClick(result: SearchResult): void {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }

    if (result.type === 'Task') {
      this.taskModalService.openTask(result.id);
    } else {
      this.router.navigate([result.route]);
    }
    this.clearSearch();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.showSearchResults = false;
    this.searchResults = [];
  }

  onSearchBlur(): void {
    this.blurTimeout = setTimeout(() => {
      this.showSearchResults = false;
    }, 200);
  }

  onSearchFocus(): void {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
    
    if (this.searchResults.length > 0 && this.searchQuery.length >= 2) {
      this.showSearchResults = true;
    }
  }

  getResultTypeColor(type: string): string {
    switch (type) {
      case 'Lead':
        return 'lead';
      case 'Client':
        return 'client';
      case 'Opportunity':
        return 'opportunity';
      case 'Task':
        return 'task';
      default:
        return '';
    }
  }

  onBranchChange(branchId: string | null): void {
    console.log('Header: Branch changed to:', branchId);
    this.branchContext.setBranchContext(branchId);
  }

  get selectedBranch(): string | null {
    return this.branchContext.selectedBranchId();
  }

  get currentPreference(): ThemePreference {
    return this.themeService.currentPreference();
  }

  onToggleSidebar(): void {
    this.sidebarToggle.emit();
  }

  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  selectTheme(theme: ThemePreference): void {
    this.themeService.setPreference(theme);
    this.showUserMenu = false;
  }

  getUserInitials(name: string): string {
    return name.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  logout(): void {
    this.authService.logout();
  }

  trackByResultId(index: number, result: SearchResult): string {
    return result.id;
  }
}